import { Injectable, inject } from '@angular/core';
import {
  GraphRequestParams,
  RainGaugeGraphRange,
} from '@cropx-common/components/graphs/models/GraphsModels';
import {
  RainGaugeStatusContent,
  RainGaugeStatusResponse,
} from '@cropx-common/components/graphs/rain-gauge-graph/models/RainGauge';
import { ApiService } from '@cropx-web/services/network/ApiService';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace SensorOverviewRainGaugePage {
  const type = 'SensorOverviewRainGaugePage';

  export class GetRainGaugeTotals {
    static readonly type = `[${type}] GetRainGaugeTotals`;

    constructor(public fieldId: string) {}
  }

  export class SetGraphRange {
    static readonly type = `[${type}] SetGraphRange`;

    constructor(public range: RainGaugeGraphRange) {}
  }

  export class SetGraphRequestParams {
    static readonly type = `[${type}] SetGraphRequestParams`;

    constructor(public params: GraphRequestParams) {}
  }
}

export interface SensorOverviewRainGaugePageModel {
  rainGaugeGraphSelectedRange: RainGaugeGraphRange;
  rainGaugeGraphParams: GraphRequestParams | null;
  rainGaugeTotals: RainGaugeStatusContent | null;
}

@State<SensorOverviewRainGaugePageModel>({
  name: 'sensorOverviewRainGauge',
  defaults: {
    rainGaugeGraphSelectedRange: RainGaugeGraphRange.Month,
    rainGaugeGraphParams: null,
    rainGaugeTotals: null,
  },
})
@Injectable()
export class SensorOverviewRainGaugeState {
  private api = inject(ApiService);

  @Action(SensorOverviewRainGaugePage.GetRainGaugeTotals)
  getRainGaugeTotals(
    ctx: StateContext<SensorOverviewRainGaugePageModel>,
    action: SensorOverviewRainGaugePage.GetRainGaugeTotals,
  ): Observable<RainGaugeStatusResponse> {
    return this.api.fieldApi.getRainGaugeStatusObservable(action.fieldId).pipe(
      tap((response: RainGaugeStatusResponse) => {
        ctx.patchState({ rainGaugeTotals: response.content });
      }),
    );
  }

  @Action(SensorOverviewRainGaugePage.SetGraphRange)
  setGraphRange(
    ctx: StateContext<SensorOverviewRainGaugePageModel>,
    action: SensorOverviewRainGaugePage.SetGraphRange,
  ): void {
    ctx.patchState({
      rainGaugeGraphSelectedRange: action.range,
    });
  }

  @Action(SensorOverviewRainGaugePage.SetGraphRequestParams)
  setGraphRequestParams(
    ctx: StateContext<SensorOverviewRainGaugePageModel>,
    action: SensorOverviewRainGaugePage.SetGraphRequestParams,
  ): void {
    ctx.patchState({
      rainGaugeGraphParams: action.params,
    });
  }
}

export class SensorOverviewRainGaugeSelectors {
  @Selector([SensorOverviewRainGaugeState])
  static graphRange(
    state: SensorOverviewRainGaugePageModel,
  ): RainGaugeGraphRange {
    return state.rainGaugeGraphSelectedRange;
  }

  @Selector([SensorOverviewRainGaugeState])
  static graphRequestParams(
    state: SensorOverviewRainGaugePageModel,
  ): GraphRequestParams {
    return state.rainGaugeGraphParams;
  }

  @Selector([SensorOverviewRainGaugeState])
  static rainGaugeTotals(
    state: SensorOverviewRainGaugePageModel,
  ): RainGaugeStatusContent {
    return state.rainGaugeTotals;
  }
}
